import { CreatorBrand } from 'constants/brands';
import React, { useCallback, useMemo } from 'react';
import {
    HeadphonesAddOutlined,
    HeadphonesCheckedOutlined,
} from '@landr/maestro';
import { t } from '@lingui/macro';
import { SampleType, toKeyRootApi, KeySign, KeyQuality } from 'types/enums';
import useExecutionEnvironment from 'hooks/useExecutionEnvironment';
import { analytics } from 'utils/analytics';
import { useGenericCarouselContext } from 'components/GenericCarousel';
import { useCreatorContext } from 'controllers/business/Creator/Creator.controller';
import { DefaultCreatorConfig } from 'controllers/business/Creator/helpers/misc.helpers';
import { ActionButton, ActionButtonProps } from 'components/ActionButton';
import { useNotificationsContext } from 'contexts/NotificationsContext/NotificationsContext';
import { Sample, Pack } from 'types';
import {
    AddedToCreatorSuccess,
    CreatorFullError,
} from 'contexts/NotificationsContext/notifications';
import { PlaceholderButton } from '../PlaceholderButton/PlaceholderButton';
import usePlaceholderButton from '../PlaceholderButton/usePlaceholderButton';

export interface CreatorButtonProps
    extends Omit<ActionButtonProps, 'icon' | 'onClick' | 'label'> {
    sample: Sample;
    callback?: () => void;
    onClick?: () => void;
    label?: string;
    isPlaceholderAllowed?: boolean;
}

export const CreatorButton: React.FC<CreatorButtonProps> = ({
    onClick,
    sample,
    label,
    isDisabled,
    isPlaceholderAllowed = false,
    ...props
}: CreatorButtonProps) => {
    const { isDAWPlugin } = useExecutionEnvironment();

    const { carouselName, carouselId } = useGenericCarouselContext();
    const {
        dispatch,
        addTrack,
        removeTrack,
        autoOpenAndPlay,
        state,
        __resumeContext,
    } = useCreatorContext();

    const { createNotification } = useNotificationsContext();

    const {
        samples,
        isSessionBpmDefined,
        isSessionKeyDefined,
        samplesInSession,
        instrumentationSessionId,
        keyRoot,
        keyQuality,
        keySign,
        bpm,
    } = state;
    const isLoop = sample.sampleType === SampleType.Loop;
    const sampleIds = [...samples.keys()];
    const isInCreator = sampleIds.includes(sample.guid);
    const numberOfSamplesInCreator = samples.size;

    const isPlaceholder = usePlaceholderButton({
        isPlaceholderAllowed,
        isActivated: isInCreator,
    });

    // CALLBACKS
    const onSampleAdded = useCallback(() => {
        createNotification(new AddedToCreatorSuccess(sample.name));
        autoOpenAndPlay();
    }, [sample.name, autoOpenAndPlay, createNotification]);

    const addSampleToCreatorDispatch = useCallback(() => {
        // Safari requires we call resumeContext here rather than furthers down
        // the function call chain
        __resumeContext();

        if (numberOfSamplesInCreator >= DefaultCreatorConfig.MAX_TRACKS) {
            createNotification(new CreatorFullError());
            return;
        }

        addTrack(sample.guid, onSampleAdded, sample.bpm);

        if (!isSessionBpmDefined && sample.bpm) {
            dispatch({ type: 'changeBPM', payload: sample.bpm });
        }

        if (!isSessionKeyDefined && sample.key) {
            const sampleKeyRootApi = toKeyRootApi(sample.key.root);
            const sampleKeyQuality = sample.key?.quality
                ? sample.key.quality
                : KeyQuality.Major;

            dispatch({
                type: 'changeKey',
                payload: {
                    root: sampleKeyRootApi,
                    quality: sampleKeyQuality,
                    sign: KeySign.Flat,
                },
            });
        }
    }, [
        addTrack,
        createNotification,
        dispatch,
        sample.guid,
        sample.bpm,
        sample.key,
        numberOfSamplesInCreator,
        onSampleAdded,
        isSessionBpmDefined,
        isSessionKeyDefined,
        __resumeContext,
    ]);

    const handleAddSampleToCreator = useCallback(() => {
        addSampleToCreatorDispatch();

        onClick && onClick();
    }, [
        onClick,
        carouselId,
        carouselName,
        addSampleToCreatorDispatch,
        numberOfSamplesInCreator,
        sample,
        instrumentationSessionId,
    ]);

    const dispatchRemoveSampleFromCreator = useCallback(() => {
        dispatch({ type: 'removeSamples', payload: [sample.guid] });

        removeTrack(sample.guid, sample.name);
    }, [dispatch, removeTrack, sample.guid]);

    const handleRemoveSampleFromCreator = useCallback(() => {
        analytics.trackCreatorUpdated({
            creatorBpm: bpm,
            creatorKey: `${keyRoot} ${keyQuality} - ${keySign}`,
            deletedCreatorSession: false,
            numberSamplesInCreator: samples.size || 0,
            sampleAdded: false,
            sampleRemoved: true,
            creatorSessionId: instrumentationSessionId,
            samplePackId: sample.pack.guid,
            samplePackName: sample.pack.name,
            assetArtist: sample.pack.artist?.guid,
            assetBpm: sample.bpm,
            assetId: sample.guid,
            assetInstrument: sample.instrumentFamily?.code,
            assetKey: sample.key?.code,
            assetLength: sample.duration || undefined,
            assetName: sample.name,
            assetGenre: (sample.pack as Pack)?.mainGenre?.code,
            assetLabel: (sample.pack as Pack)?.label?.guid || undefined,
            assetLoopType: sample.sampleType as SampleType,
        });

        dispatchRemoveSampleFromCreator();
    }, [
        dispatchRemoveSampleFromCreator,
        sample,
        bpm,
        keyRoot,
        keyQuality,
        keySign,
        samples,
        instrumentationSessionId,
        samplesInSession,
    ]);

    const handleOnClick = useCallback(
        (event: React.MouseEvent) => {
            event.preventDefault();
            event.stopPropagation();

            if (isInCreator) {
                handleRemoveSampleFromCreator();
            } else {
                handleAddSampleToCreator();
            }
        },
        [isInCreator, handleRemoveSampleFromCreator, handleAddSampleToCreator],
    );

    const labelText = useMemo(() => {
        if (label) {
            return label;
        }
        if (isLoop) {
            return isInCreator
                ? t`Remove from ${CreatorBrand}`
                : t`Add to ${CreatorBrand}`;
        }

        return t`${CreatorBrand} currently only supports loops`;
    }, [label, isLoop, isInCreator]);

    if (isDAWPlugin) {
        return null;
    }

    const creatorDisabled = isDisabled || !isLoop;

    return isPlaceholder ? (
        <PlaceholderButton />
    ) : (
        <ActionButton
            data-component="CreatorButton"
            data-isdownloaded={sample.isDownloaded}
            aria-label={labelText}
            label={labelText}
            kind={creatorDisabled ? 'minimal' : 'solid'}
            isDisabled={creatorDisabled}
            icon={
                isInCreator ? HeadphonesCheckedOutlined : HeadphonesAddOutlined
            }
            onClick={handleOnClick}
            {...props}
        />
    );
};

export default CreatorButton;
